import './style.css'
import { loadPlaces, getPlaces, byId, lockedCount, applyPaid, nearbyLocked } from './data.js'
import { initMap, setMarkers, highlightPin, fitToPlaces } from './map.js'
import { initFilters, applyFilters, refreshControls, activeQuartierLabel, setTab } from './filters.js'
import { initList, renderList, highlightCard } from './list.js'
import { initModal, openDetail, openPaywall, openRestore, showRestoreSent, showRestoreError, showUnlocking, showUnlockError, showCheckoutError, closeModal } from './modal.js'
import { restoreUnlock, unlockWithKey, isUnlocked, requestRestore, consumeMagic } from './unlock.js'
import { openCheckout, closeCheckout } from './checkout.js'

let userPos = null // set when the buyer uses "locate me" — powers nearby copy

async function start() {
  await loadPlaces()

  const cached = await restoreUnlock((fresh, reason) => {
    applyPaid(fresh); refresh(); updateUnlockUI(reason === 'invalid')
  })
  if (cached) applyPaid(cached)

  initModal({ onSave: refresh })
  initList({
    onHover: (id) => highlightPin(id, true),
    onLeave: (id) => highlightPin(id, false),
    onOpen: (id) => openDetail(byId(id)),
    onLockedOpen: () => showPaywall(),
    onSaveChange: refresh,
  })

  initMap(() => {
    initFilters(refresh)
    refresh()
    setupMobileToggle()
    initWelcome()
    handleMagicUnlock()
    handleDeepLink()
    handleCheckoutLink()
  }, {
    onHoverPin: (id) => highlightCard(id, true),
    onLeavePin: (id) => highlightCard(id, false),
    onOpen: (id) => openDetail(byId(id)),
    onLockedOpen: () => showPaywall(),
    onGeolocate: (pos) => { userPos = pos; updateUnlockUI() },
  })
}

// Deep link: ?place=<id> opens that spot (full if free/unlocked, paywall if
// locked) and centres the map on it — so a shared link lands you right there.
function handleDeepLink() {
  const id = new URLSearchParams(location.search).get('place')
  if (!id) return
  const p = byId(id)
  if (!p) return
  window.peTrack?.('deep_link_open', { place_id: id, locked: !!p.locked })
  fitToPlaces([p])
  if (p.locked) showPaywall()
  else openDetail(p)
}

// ?buy=1 from a "buy now" email CTA — jump straight to the Paddle checkout.
// Skipped if they've already unlocked (nothing left to buy).
function handleCheckoutLink() {
  if (!new URLSearchParams(location.search).get('buy')) return
  if (isUnlocked() || lockedCount() === 0) return
  startCheckout()
}

// "Top 10" welcome — frames the free tier as Richard's curated lead magnet.
// Shows on a fresh visit (until dismissed) and always when arriving via the
// email link (?guide=top10), which also pre-selects the Top 10 filter.
const WELCOME_LS = 'pe_welcome_seen'
function initWelcome() {
  const banner = document.getElementById('welcome')
  if (!banner) return
  const fromEmail = new URLSearchParams(location.search).get('guide') === 'top10'
  if (fromEmail) showTop10()

  const dismissed = localStorage.getItem(WELCOME_LS)
  if (!fromEmail && (dismissed || isUnlocked())) return
  banner.hidden = false

  document.getElementById('welcome-cta').onclick = () => { showTop10(); dismissWelcome() }
  document.getElementById('welcome-close').onclick = dismissWelcome
}
function showTop10() {
  window.peTrack?.('select_top10')
  setTab('top10'); refreshControls(); refresh(true)
}
function dismissWelcome() {
  const banner = document.getElementById('welcome')
  if (banner) banner.hidden = true
  localStorage.setItem(WELCOME_LS, '1')
}

function refresh(fit = false) {
  const filtered = applyFilters(getPlaces())
  renderList(filtered)
  setMarkers(filtered)
  refreshControls()
  updateCount(filtered)
  updateUnlockUI()
  if (fit && filtered.length) fitToPlaces(filtered)
}

function updateCount(filtered) {
  const where = activeQuartierLabel()
  const n = filtered.length
  document.getElementById('count').innerHTML =
    `Showing <b>${n}</b> place${n === 1 ? '' : 's'}${where ? ` in <b>${where}</b>` : ''}`
}

// ---- unlock ----
function paywallActions() {
  const nearby = userPos ? nearbyLocked(userPos).length : 0
  return { onBuy: startCheckout, onRestore: openRestoreFlow, nearby, total: lockedCount() }
}

// Open the paywall + log a GA4 funnel event (no-op until analytics is live).
function showPaywall() {
  window.peTrack?.('view_paywall', { locked: lockedCount() })
  openPaywall(paywallActions())
}

// Paddle overlay checkout → on success the transaction id unlocks instantly,
// no typing. Falls back to restore if Paddle isn't wired yet or validation trips.
function startCheckout() {
  window.peTrack?.('begin_checkout', { currency: 'USD', value: 39 })
  openCheckout(
    (txn) => finishUnlock(txn),
    (detail) => showCheckoutError(detail, startCheckout),
  ).catch(() => openRestoreFlow())
}

// Confirm the purchase with the Worker. Paddle's transaction status settles a
// moment after checkout completes, so retry for a few seconds before giving up.
async function finishUnlock(txn) {
  showUnlocking()
  for (let attempt = 0; attempt < 6; attempt++) {
    try {
      const paid = await unlockWithKey(txn)
      window.peTrack?.('purchase', { transaction_id: txn, currency: 'USD', value: 39 })
      applyPaid(paid); closeCheckout(); closeModal(); refresh(); showUnlockedBanner()
      return
    } catch (err) {
      // 'invalid' here usually means the status hasn't settled yet — wait + retry.
      if (err.code === 'invalid' && attempt < 5) {
        await new Promise((r) => setTimeout(r, 2500))
        continue
      }
      showUnlockError(() => finishUnlock(txn), openRestoreFlow)
      return
    }
  }
}

function openRestoreFlow() {
  openRestore((email) => {
    if (!email || !/.+@.+\..+/.test(email)) return showRestoreError('Enter a valid email.')
    window.peTrack?.('restore_requested')
    requestRestore(email)
    showRestoreSent(email)
  })
}

// ?unlock=<token> from the magic-link email — consume it, cache the paid data,
// and store the buyer's txn so future loads stay unlocked.
async function handleMagicUnlock() {
  const token = new URLSearchParams(location.search).get('unlock')
  if (!token) return
  try {
    const paid = await consumeMagic(token)
    window.peTrack?.('magic_unlock')
    applyPaid(paid); refresh(); showUnlockedBanner()
  } catch { /* expired/invalid link — they can request another */ }
}

function updateUnlockUI() {
  const bar = document.getElementById('unlockbar')
  const n = lockedCount()
  const done = isUnlocked() || n === 0
  document.body.classList.toggle('unlocked', done)
  if (done) { bar.hidden = true; return }
  const near = userPos ? nearbyLocked(userPos).length : 0
  document.getElementById('unlockbar-text').innerHTML = near
    ? `<b>${near} spots near you</b> are locked — unlock the full map`
    : `<b>${n} more spots</b> locked — unlock the full map`
  document.getElementById('unlockbar-btn').onclick = () => showPaywall()
  bar.hidden = false
}

function showUnlockedBanner() {
  let b = document.getElementById('unlockedbanner')
  if (!b) { b = document.createElement('div'); b.id = 'unlockedbanner'; document.getElementById('app').appendChild(b) }
  b.textContent = '✓ All spots unlocked — enjoy the full map'
  b.hidden = false; b.classList.add('show')
  setTimeout(() => b.classList.remove('show'), 4200)
}

// ---- mobile map/list toggle ----
function setupMobileToggle() {
  const btn = document.getElementById('viewtoggle')
  const label = () => {
    const onMap = document.body.classList.contains('view-map')
    btn.innerHTML = `<span>${onMap ? '☰ List' : '◍ Map'}</span>`
  }
  label()
  btn.onclick = () => { document.body.classList.toggle('view-map'); label() }
}

start()
